import React, { useState, useEffect, useRef } from 'react';

/**
 * Generic EditableCell Component for Log/Tracking tables
 * 
 * @param {Object} props
 * @param {*} props.value - Current cell value
 * @param {Object} props.row - Row data object (must have .id)
 * @param {Object} props.column - Column config (key, type, options)
 * @param {Function} props.onSave - Save handler: (rowId, columnKey, value) => Promise
 * @param {boolean} props.disabled - Whether editing is disabled
 * @param {Function} props.formatValue - Optional: (val, column) => string for display
 * @param {Function} props.renderValue - Optional: (val, column) => ReactNode for custom rendering
 * @param {string} props.theme - Optional: 'light' (default) or 'dark'
 * @param {string} props.accentColor - Optional: border color when editing (default: '#007bff')
 */
const EditableCell = ({ 
  value, 
  row, 
  column, 
  onSave, 
  disabled = false,
  formatValue,
  renderValue,
  theme = 'light',
  accentColor = '#007bff',
  cellBackgroundColor,  // Optional: function (value, column) => color string, or static color
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);

  // Sync with external value changes
  useEffect(() => {
    setEditValue(value ?? '');
  }, [value]);

  // Auto-focus and select on edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) {
        inputRef.current.select();
      }
    }
  }, [isEditing]);

  // Determine if cell is editable
  const isEditable = !disabled && column.type !== 'computed';

  const handleClick = () => {
    if (isEditable && !isEditing) {
      setIsEditing(true);
      setEditValue(value ?? '');
    }
  };

  const handleBlur = async () => {
    setIsEditing(false);
    const normalizedValue = value ?? '';
    const normalizedEditValue = editValue ?? '';

    if (String(normalizedEditValue) !== String(normalizedValue)) {
      setIsSaving(true);
      try {
        await onSave(row.id, column.key, editValue);
      } catch (error) {
        console.error('Save failed:', error);
        setEditValue(value ?? '');
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleBlur();
    } else if (e.key === 'Escape') {
      setEditValue(value ?? '');
      setIsEditing(false);
    }
  };

  // Default value formatting
  const defaultFormatValue = (val) => {
    if (val === null || val === undefined || val === '') return '-';
    
    if (column.type === 'date' && val) {
      try {
        const date = new Date(val);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString();
        }
      } catch {
        return val;
      }
    }

    if (column.type === 'datetime' && val) {
      try {
        const date = new Date(val);
        if (!isNaN(date.getTime())) {
          return date.toLocaleString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
        }
      } catch {
        return val;
      }
    }
    
    if (column.type === 'number' && val !== null) {
      // Check if formatting is needed (has decimals in column config)
      if (column.decimals !== undefined) {
        return Number(val).toFixed(column.decimals);
      }
      return val;
    }
    
    return val;
  };

  // Get display value
  const getDisplayValue = () => {
    if (formatValue) {
      return formatValue(value, column);
    }
    return defaultFormatValue(value);
  };

  // Theme-based styles
  const isDark = theme === 'dark';
  const isTextarea = column.type === 'textarea';
  
  // Determine cell background color
  let bgColor = 'transparent';
  if (isSaving) {
    bgColor = '#fff3cd';
  } else if (cellBackgroundColor) {
    bgColor = typeof cellBackgroundColor === 'function' 
      ? cellBackgroundColor(value, column) 
      : cellBackgroundColor;
  }
  
  const styles = {
    input: {
      width: '100%',
      padding: '4px',
      border: `2px solid ${accentColor}`,
      borderRadius: '4px',
      fontSize: '0.85rem',
      boxSizing: 'border-box',
      backgroundColor: isDark ? '#1a1a2e' : 'white',
      color: isDark ? '#fff' : 'inherit',
    },
    cell: {
      padding: '4px',
      minHeight: '24px',
      cursor: isEditable ? 'pointer' : 'default',
      backgroundColor: bgColor,
      borderRadius: '2px',
      overflow: isTextarea ? 'visible' : 'hidden',
      textOverflow: isTextarea ? 'clip' : 'ellipsis',
      whiteSpace: isTextarea ? 'pre-wrap' : 'nowrap',
      wordWrap: isTextarea ? 'break-word' : 'normal',
      lineHeight: isTextarea ? '1.4' : 'inherit',
      color: isDark ? '#e0e0e0' : 'inherit',
    },
  };

  // Editing mode - render input/select based on column type
  if (isEditing) {
    // Select dropdown
    if (column.type === 'select' && column.options) {
      return (
        <select
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          style={styles.input}
        >
          <option value="">--</option>
          {column.options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }

    // Date input
    if (column.type === 'date') {
      const dateValue = editValue ? String(editValue).split('T')[0] : '';
      return (
        <input
          ref={inputRef}
          type="date"
          value={dateValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          style={styles.input}
        />
      );
    }

    // Number input
    if (column.type === 'number') {
      return (
        <input
          ref={inputRef}
          type="number"
          step={column.step || '0.01'}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          style={styles.input}
        />
      );
    }

    // Textarea input
    if (column.type === 'textarea') {
      return (
        <textarea
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            // Only Escape closes textarea, Enter creates newline
            if (e.key === 'Escape') {
              setEditValue(value ?? '');
              setIsEditing(false);
            }
          }}
          style={{
            ...styles.input,
            minHeight: '60px',
            resize: 'vertical',
          }}
        />
      );
    }

    // Datetime input
    if (column.type === 'datetime') {
      return (
        <input
          ref={inputRef}
          type="datetime-local"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          style={styles.input}
        />
      );
    }

    // Default: text input
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        style={styles.input}
      />
    );
  }

  // Display mode
  // Check for custom render function first
  if (renderValue) {
    const customRender = renderValue(value, column);
    if (customRender !== null && customRender !== undefined) {
      return (
        <div
          onClick={handleClick}
          style={styles.cell}
          title={isEditable ? 'Click to edit' : ''}
        >
          {customRender}
        </div>
      );
    }
  }

  return (
    <div
      onClick={handleClick}
      style={styles.cell}
      title={isEditable ? 'Click to edit' : ''}
    >
      {getDisplayValue()}
    </div>
  );
};

export default EditableCell;
